import { DatePipe } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription, interval, lastValueFrom } from 'rxjs';
import { PageableRequest } from 'src/app/admin-api';
import { PageContent, TableColumn, TableComponent } from 'src/app/components/table';
import {
  BillingCycleAttemptSummary,
  BillingCycleEnqueueLoteResult,
  BillingCycleLoteItem,
  BillingCycleMonthSummary,
  BillingCycleService,
  BillingCycleSubscriberPaymentStep
} from 'src/app/services/billing-cycle.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

const STEP_LABELS: Record<number, string> = {
  0: 'VALIDATION',
  1: 'PAYMENT',
  2: 'FAILED',
  3: 'PAID',
  4: 'ACTIVATION',
  5: 'SUBSCRIBER_EDITION',
  6: 'FINISHED'
};

const POLL_MS = 60_000;

@Component({
  selector: 'app-billing-cycle',
  templateUrl: './billing-cycle.component.html',
  styleUrls: ['./billing-cycle.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [DatePipe]
})
export class BillingCycleComponent implements OnInit, OnDestroy {
  billingCycleService = inject(BillingCycleService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private title = inject(Title);
  private datePipe = inject(DatePipe);

  @ViewChild('stepsTable') stepsTable?: TableComponent;

  monthSummary: BillingCycleMonthSummary | null = null;
  lotes: BillingCycleLoteItem[] = [];
  isActionLoading = false;
  private refreshSub: Subscription | null = null;

  enqueueAttempt = 1;
  enqueueBatchSize = 1000;
  enqueueOnlyMonthly = false;
  enqueueMaxBatches = 1;
  filterAttempt: number | null = null;
  filterPaymentRoundId: number | null = null;
  filterStep: number | null = null;
  selectedAttempt: number | null = null;
  visibleAttempts: BillingCycleAttemptSummary[] = [];

  stepOptions = [
    { label: 'Todos', value: null },
    { label: 'VALIDATION (0)', value: 0 },
    { label: 'PAYMENT (1)', value: 1 },
    { label: 'FAILED (2)', value: 2 },
    { label: 'PAID (3)', value: 3 },
    { label: 'ACTIVATION (4)', value: 4 },
    { label: 'SUBSCRIBER_EDITION (5)', value: 5 },
    { label: 'FINISHED (6)', value: 6 }
  ];

  cols: TableColumn[] = [
    new TableColumn.Builder()
      .setHeader('Subscriber')
      .setField('subscriberId')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Lote')
      .setField('paymentRoundId')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Step')
      .setField('step')
      .setCondition('equals')
      .setDisplayFunction((item: BillingCycleSubscriberPaymentStep) =>
        this.formatStep(item.step)
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Attempt')
      .setField('attempt')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('CardPriority')
      .setField('cardPriority')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('AnnualMonthly')
      .setField('annualMonthly')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('GatewayPriorityId')
      .setField('gatewayPriorityId')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Origin')
      .setField('origin')
      .setCondition('startsWith')
      .build(),
    new TableColumn.Builder()
      .setHeader('Priority')
      .setField('priority')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('CCP')
      .setField('creditCardPaymentId')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('LastMessage')
      .setField('lastMessage')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Atualizado')
      .setField('dateUpdated')
      .setType('date')
      .setCondition('between')
      .setDisplayFunction((item: BillingCycleSubscriberPaymentStep) =>
        item.dateUpdated
          ? this.datePipe.transform(item.dateUpdated, 'dd/MM/yyyy HH:mm') ?? '-'
          : '-'
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Criado')
      .setField('dateCreated')
      .setType('date')
      .setCondition('between')
      .setDisplayFunction((item: BillingCycleSubscriberPaymentStep) =>
        item.dateCreated
          ? this.datePipe.transform(item.dateCreated, 'dd/MM/yyyy HH:mm') ?? '-'
          : '-'
      )
      .build()
  ];

  loadSteps = async (
    request: PageableRequest
  ): Promise<PageContent<BillingCycleSubscriberPaymentStep>> => {
    return lastValueFrom(
      this.billingCycleService.listSubscriberPaymentSteps(
        request,
        this.filterStep,
        this.filterPaymentRoundId,
        this.filterAttempt
      )
    );
  };

  constructor() {
    this.title.setTitle('Ciclo de Cobrança');
  }

  async ngOnInit(): Promise<void> {
    await this.refreshAll();
    this.refreshSub = interval(POLL_MS).subscribe(() => {
      void this.refreshAll(false);
    });
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
  }

  async refreshAll(showLoader = true): Promise<void> {
    if (showLoader) {
      LoaderService.showLoader();
    }
    try {
      const [monthResult, lotesResult] = await Promise.allSettled([
        lastValueFrom(this.billingCycleService.getMonthSummary(this.enqueueAttempt)),
        lastValueFrom(this.billingCycleService.listLotes())
      ]);

      if (monthResult.status === 'fulfilled') {
        this.monthSummary = monthResult.value;
        this.visibleAttempts = monthResult.value.byAttempt ?? [];
      } else if (showLoader) {
        this.monthSummary = null;
        this.visibleAttempts = [];
      }
      if (lotesResult.status === 'fulfilled') {
        this.lotes = lotesResult.value;
      }

      const firstError = [monthResult, lotesResult].find(
        (result): result is PromiseRejectedResult => result.status === 'rejected'
      );
      if (firstError && showLoader) {
        AppDialogService.showErrorDialog(firstError.reason);
      }
    } finally {
      if (showLoader) {
        LoaderService.showLoader(false);
      }
    }
  }

  async refreshMonthOnly(): Promise<void> {
    try {
      this.monthSummary = await lastValueFrom(
        this.billingCycleService.getMonthSummary(this.enqueueAttempt)
      );
      this.visibleAttempts = this.monthSummary.byAttempt ?? [];
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  confirmEnqueue(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Enfileirar lote(s)? Attempt=${this.enqueueAttempt}, batch=${this.enqueueBatchSize}, maxBatches=${this.enqueueMaxBatches}, onlyMonthly=${this.enqueueOnlyMonthly}`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Enfileirar',
      rejectLabel: 'Cancelar',
      accept: () => {
        void this.runEnqueue();
      }
    });
  }

  private async runEnqueue(): Promise<void> {
    this.isActionLoading = true;
    LoaderService.showLoader();
    try {
      const result: BillingCycleEnqueueLoteResult = await lastValueFrom(
        this.billingCycleService.enqueueLote({
          attempt: this.enqueueAttempt,
          batchSize: this.enqueueBatchSize,
          onlyMonthly: this.enqueueOnlyMonthly,
          maxBatches: this.enqueueMaxBatches
        })
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Lote enfileirado',
        detail: `Enqueued=${result.totalEnqueued ?? 0}, batches=${result.batchesExecuted ?? 0}, remaining=${result.remainingEligibleCount ?? 0}, lastRound=${result.lastPaymentRoundId ?? '-'}`
      });
      await this.refreshAll(false);
      this.reloadStepsTable();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      this.isActionLoading = false;
      LoaderService.showLoader(false);
    }
  }

  applyStepFilters(): void {
    this.reloadStepsTable();
  }

  selectAttempt(row: BillingCycleAttemptSummary): void {
    this.selectedAttempt = row.attempt ?? null;
    this.filterAttempt = row.attempt ?? null;
    this.reloadStepsTable();
  }

  filterByLote(lote: BillingCycleLoteItem): void {
    this.filterPaymentRoundId = lote.paymentRoundId ?? null;
    if (lote.maxAttempt != null) {
      this.filterAttempt = lote.maxAttempt;
    }
    this.reloadStepsTable();
  }

  private reloadStepsTable(): void {
    void this.stepsTable?.refresh();
  }

  formatNumber(value?: number | null): string {
    if (value == null) {
      return '-';
    }
    if (value < 0) {
      return 'n/d';
    }
    return value.toLocaleString('pt-BR');
  }

  formatApproval(value?: number | null): string {
    if (value == null) {
      return '—';
    }
    return `${value.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    })}%`;
  }

  formatMargin(value?: number | null): string {
    if (value == null) {
      return '—';
    }
    const formatted = Math.abs(value).toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
    if (value > 0) {
      return `+${formatted} pp`;
    }
    if (value < 0) {
      return `-${formatted} pp`;
    }
    return `${formatted} pp`;
  }

  private formatStep(step?: number | string): string {
    if (step == null) {
      return '-';
    }
    if (typeof step === 'string') {
      return step;
    }
    return STEP_LABELS[step] ?? String(step);
  }
}
